// src/features/employee/Employee.js

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import "../client/Client.css"; // reuse table/header styles
import AddEmployee from "./AddEmployee";
import config from "../../config";
import { CiCirclePlus } from "react-icons/ci";
import { IoArrowBack } from "react-icons/io5";
import AddCandidate from "../recruitment/Onboarding/Addedit";
import EmployeeProfile from "./EmployeeProfile";
import EmployeeAttendance from "./EmployeeAttendance";
import EmployeePayslip from "./EmployeePayslip";
import EmployeeLeave from "./EmployeeLeave";
import EmployeeFilter from "./EmployeeFilter";
import { IoSearchOutline } from "react-icons/io5";
import { BsSliders } from "react-icons/bs";
import Header from "../../components/common/Header";

/* ─── Upload Employee Modal ─────────────────────────────────── */
const UploadEmployeeModal = ({ onClose, token, orgId }) => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState({ text: "", type: "" });
  const dropRef = useRef(null);
  const fileInputRef = useRef(null);

  // Fetch clients
  useEffect(() => {
    if (!token || !orgId) return;
    fetch(`${config.API_BASE_URL}${config.CLIENT_LIST_ENDPOINT}${orgId}`, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    })
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((data) => {
        const list = Array.isArray(data) ? data : (data.body || []);
        setClients(list);
      })
      .catch(() => { });
  }, [token, orgId]);

  // Drag & Drop handlers
  const onDragOver = useCallback((e) => { e.preventDefault(); setDragOver(true); }, []);
  const onDragLeave = useCallback(() => setDragOver(false), []);
  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) setSelectedFile(file);
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setSelectedFile(file);
    e.target.value = null;
  };

  // Download template
  const handleDownloadTemplate = async () => {
    try {
      const res = await fetch(
        `${config.API_BASE_URL}${config.BULK_EMPLOYEE_TEMPLATE_ENDPOINT}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "employee_template.xlsx";
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Template download error:", err);
    }
  };

  // Upload
  const handleUpload = async () => {
    if (!selectedFile) { setUploadMsg({ text: "Please select a file first.", type: "error" }); return; }
    setUploading(true);
    setUploadMsg({ text: "", type: "" });
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      if (selectedClient) formData.append("clientKey", selectedClient);
      formData.append("orgKey", orgId);

      const queryParams = new URLSearchParams();
      if (orgId) queryParams.append("orgKey", orgId);
      if (selectedClient) queryParams.append("clientKey", selectedClient);

      const res = await fetch(
        `${config.API_BASE_URL}${config.BULK_EMPLOYEE_UPLOAD_ENDPOINT}?${queryParams.toString()}`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (data.failedCount > 0) {
          const firstError = data.failedEmployees?.[0];
          const errorDetail = firstError ? `(${firstError.emailAddress}: ${firstError.reason})` : "";
          const msg = data.successCount > 0
            ? `Partial success: ${data.successCount} uploaded, ${data.failedCount} failed. ${errorDetail}`
            : `Upload failed for ${data.failedCount} record(s). ${errorDetail}`;
          setUploadMsg({ text: msg, type: "error" });
        } else {
          setUploadMsg({ 
            text: data.successCount ? `Successfully uploaded ${data.successCount} employees!` : (data.message || "Employees uploaded successfully!"), 
            type: "success" 
          });
          setTimeout(onClose, 1800);
        }
      } else {
        setUploadMsg({ text: data.message || "Upload failed. Please try again.", type: "error" });
      }
    } catch (err) {
      setUploadMsg({ text: "Network error. Please try again.", type: "error" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        {/* Title */}
        <h2 style={styles.modalTitle}>Upload employee</h2>

        {/* Client dropdown */}
        <label style={styles.label}>Client</label>
        <div style={styles.selectWrapper}>
          <select
            style={styles.select}
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
          >
            <option value=""></option>
            {clients.map((c) => (
              <option key={c.clientKey} value={c.clientKey}>{c.name}</option>
            ))}
          </select>
          <span style={styles.selectArrow}>▼</span>
        </div>

        {/* Download button */}
        <button style={styles.downloadBtn} onClick={handleDownloadTemplate}>
          Download excel template
        </button>

        {/* Drag & Drop zone */}
        <div
          ref={dropRef}
          style={{ ...styles.dropZone, ...(dragOver ? styles.dropZoneActive : {}) }}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div style={styles.dropIconCircle}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#00bcd4" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>
          {selectedFile ? (
            <p style={styles.dropText}>
              <span style={{ color: "#111", fontWeight: 600 }}>{selectedFile.name}</span>
            </p>
          ) : (
            <p style={styles.dropText}>
              Drag &amp; Drop{" "}
              <span style={{ color: "#e05c5c", textDecoration: "underline" }}>employee details</span>
              {" "}to upload
            </p>
          )}
          <p style={styles.dropSubText}>Supported formats: .csv</p>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onChange={handleFileChange}
        />

        {/* Upload message */}
        {uploadMsg.text && (
          <p style={{ fontSize: "12px", marginTop: "8px", color: uploadMsg.type === "success" ? "#16a34a" : "#dc2626", textAlign: "center" }}>
            {uploadMsg.text}
          </p>
        )}

        {/* Action buttons */}
        <div style={styles.actionRow}>
          <button style={styles.cancelBtn} onClick={onClose} disabled={uploading}>Cancel</button>
          <button style={styles.uploadBtn} onClick={handleUpload} disabled={uploading}>
            {uploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Modal Styles ──────────────────────────────────────────── */
const styles = {
  overlay: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.35)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    background: "#fff",
    borderRadius: "16px",
    padding: "32px 28px 24px",
    width: "380px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
    display: "flex", flexDirection: "column", gap: "14px",
  },
  modalTitle: {
    margin: 0, fontSize: "18px", fontWeight: "700", color: "#111827",
  },
  label: {
    fontSize: "13px", fontWeight: "500", color: "#374151", marginBottom: "-8px",
  },
  selectWrapper: {
    position: "relative",
  },
  select: {
    width: "100%", padding: "10px 36px 10px 14px",
    border: "1.5px solid #d1d5db", borderRadius: "8px",
    appearance: "none", fontSize: "14px", color: "#111",
    background: "#fff", cursor: "pointer", outline: "none",
  },
  selectArrow: {
    position: "absolute", right: "14px", top: "50%",
    transform: "translateY(-50%)", fontSize: "10px",
    color: "#6b7280", pointerEvents: "none",
  },
  downloadBtn: {
    width: "100%", padding: "12px",
    background: "linear-gradient(135deg, #232d5c, #232d5c)",
    color: "#fff", border: "none", borderRadius: "8px",
    fontSize: "14px", fontWeight: "600", cursor: "pointer",
    letterSpacing: "0.3px",
  },
  dropZone: {
    border: "1.8px dashed #93c5fd",
    borderRadius: "10px",
    padding: "24px 16px",
    display: "flex", flexDirection: "column", alignItems: "center",
    gap: "8px", cursor: "pointer",
    background: "#f0f9ff",
    transition: "background 0.2s, border-color 0.2s",
  },
  dropZoneActive: {
    background: "#dbeafe", borderColor: "#3b82f6",
  },
  dropIconCircle: {
    width: "52px", height: "52px",
    borderRadius: "50%", background: "#e0f7fa",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  dropText: {
    margin: 0, fontSize: "13px", color: "#374151", textAlign: "center",
  },
  dropSubText: {
    margin: 0, fontSize: "11px", color: "#9ca3af",
  },
  actionRow: {
    display: "flex", gap: "12px", marginTop: "4px",
  },
  cancelBtn: {
    flex: 1, padding: "11px",
    background: "#fff", border: "1.5px solid #d1d5db",
    borderRadius: "8px", fontSize: "14px", fontWeight: "600",
    color: "#374151", cursor: "pointer",
  },
  uploadBtn: {
    flex: 1, padding: "11px",
    background: "linear-gradient(135deg, #232d5c, #232d5c)",
    border: "none", borderRadius: "8px",
    fontSize: "14px", fontWeight: "600", color: "#fff", cursor: "pointer",
  },
};

/* ─── Main Employee Component ───────────────────────────────── */
const Employee = ({ setActivePage }) => {
  // --- user info pulled from localStorage ---
  const [userDetails, setUserDetails] = useState({
    name: "",
    role: "",
    token: "",
    orgId: "",
  });
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [showTabs, setShowTabs] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [showBulkModal, setShowBulkModal] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("Authorization") || "";
    const firstName = localStorage.getItem("firstName") || "";
    const lastName = localStorage.getItem("lastName") || "";
    const role = localStorage.getItem("role")?.toUpperCase() || "GUEST";
    const orgId = localStorage.getItem("orgId") || "";

    setUserDetails({
      name: `${firstName} ${lastName}`.trim(),
      role,
      token,
      orgId,
    });
  }, []);

  // --- data / ui state ---
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const itemsPerPage = 8;


  useEffect(() => {
    if (!userDetails.token || !userDetails.orgId) return;

    // Ignore responses from a previous page/search if the user has moved on
    let cancelled = false;

    const fetchEmployees = async () => {
      setLoading(true);

      try {
        const params = new URLSearchParams({
          orgKey: userDetails.orgId,
          sort: "id,desc",
          page: currentPage,
          size: itemsPerPage
        });

        if (searchTerm) params.append("search", searchTerm);

        // ✅ Add filter params dynamically
        if (appliedFilters.gender)
          params.append("gender", appliedFilters.gender);

        if (appliedFilters.clientName)
          params.append("clientKey", appliedFilters.clientName);

        if (appliedFilters.subVendor)
          params.append("subVendor", appliedFilters.subVendor);

        if (appliedFilters.jobRole)
          params.append("jobRole", appliedFilters.jobRole);

        if (appliedFilters.workSite)
          params.append("workSite", appliedFilters.workSite);

        if (appliedFilters.status)
          params.append("status", appliedFilters.status);

        if (appliedFilters.sortBy)
          params.append("sortBy", appliedFilters.sortBy);

        const response = await fetch(
          `${config.API_BASE_URL}${config.GET_VIEW_EMPLOYEES_ENDPOINT}?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${userDetails.token}`,
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        );

        const data = await response.json();
        if (cancelled) return;

        if (!response.ok) {
          throw new Error(data.message || "Failed to fetch employees");
        }

        let rawContent = data.content || data.body || [];
        let content = Array.isArray(rawContent)
          ? rawContent
          : (Array.isArray(rawContent.content) ? rawContent.content : (Array.isArray(rawContent.employees) ? rawContent.employees : []));

        const normalized = content.map((emp) => ({
          employeeId: emp.employeeId,
          name: emp.employeeName,
          clientName: emp.clientName || "-",
          key: emp.employeeCode,
          site: emp.workSite || "—",
          role: emp.jobRole || "—",
          status: emp.status || "Active",
        }));

        setEmployees(normalized);
        const totalCount = data.totalElements || (data.body && data.body.totalEmployees) || normalized.length;
        setTotalElements(totalCount);
        setTotalPages(data.totalPages || (data.body && data.body.totalPages) || Math.ceil(totalCount / itemsPerPage));
        setError(null);

      } catch (err) {
        if (cancelled) return;
        console.error("Employee fetch error:", err);
        setError(err.message || "Unexpected error");
        setEmployees([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchEmployees();
    return () => {
      cancelled = true;
    };
  }, [
    userDetails.token,
    userDetails.orgId,
    refetchTrigger,
    searchTerm,
    currentPage,
    appliedFilters
  ]);

  // --- handlers for Add Employee ---
  const handleAddEmployee = () => {
    setShowAddEmployee(true);
    setSelectedEmployee(null); // Clear any selected employee for add mode
  };

  const handleCancelAddEmployee = () => {
    setShowAddEmployee(false);
    setSelectedEmployee(null);
    setRefetchTrigger(prev => prev + 1);
  };

  const fetchEmployeeDetails = async (employeeId) => {
    try {
      const token = localStorage.getItem("Authorization");

      const response = await fetch(
        `${config.API_BASE_URL}${config.GET_EMPLOYEE_DETAIL_ENDPOINT}${employeeId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        const employeeData = data.body || data.data || data;
        setSelectedEmployee(employeeData);
        setShowAddEmployee(true);
      } else {
        console.error("Failed to fetch employee details");
      }
    } catch (error) {
      console.error("Failed to fetch employee details:", error);
    }
  };

  // --- search handler ---
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0); // Reset to first page on search
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const filteredEmployees = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((emp) => {
      return (
        (emp.name || "").toLowerCase().includes(q) ||
        (emp.clientName || "").toLowerCase().includes(q) ||
        (emp.key || "").toLowerCase().includes(q) ||
        (emp.site || "").toLowerCase().includes(q) ||
        (emp.role || "").toLowerCase().includes(q) ||
        (emp.status || "").toLowerCase().includes(q)
      );
    });
  }, [employees, searchTerm]);

  // Calculate current page items
  const indexOfFirstItem = currentPage * itemsPerPage;
  const indexOfLastItem = Math.min(indexOfFirstItem + itemsPerPage, totalElements);
  const currentEmployees = filteredEmployees;

  const handleFilter = () => {
    setShowFilter(true);
  };

  const handleCloseFilter = () => {
    setShowFilter(false);
  };

  const handleApplyFilter = (filters) => {
    setAppliedFilters(filters);
    setCurrentPage(0);
    setRefetchTrigger(prev => prev + 1);
    setShowFilter(false);
  };

  // --- determine header title based on current view ---
  const getHeaderTitle = () => {
    if (showAddEmployee) return selectedEmployee ? "Edit Employee" : "Add New Employee";
    if (showTabs) return "Employee Management";
    return "Employee List";
  };

  const handleEditEmployee = (id) => {
    setSelectedEmployeeId(id);
    fetchEmployeeDetails(id);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage < 4) {
        for (let i = 0; i < 5; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
        pageNumbers.push(totalPages - 1);
      } else if (currentPage > totalPages - 5) {
        pageNumbers.push(0);
        pageNumbers.push("...");
        for (let i = totalPages - 5; i < totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(0);
        pageNumbers.push("...");
        pageNumbers.push(currentPage - 1);
        pageNumbers.push(currentPage);
        pageNumbers.push(currentPage + 1);
        pageNumbers.push("...");
        pageNumbers.push(totalPages - 1);
      }
    }
    return pageNumbers;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "profile":
        return <EmployeeProfile employeeId={selectedEmployeeId} />;
      case "attendance":
        return <EmployeeAttendance employeeId={selectedEmployeeId} />;
      case "payslip":
        return <EmployeePayslip employeeId={selectedEmployeeId} />;
      case "leave":
        return <EmployeeLeave employeeId={selectedEmployeeId} />;
      default:
        return <EmployeeProfile employeeId={selectedEmployeeId} />;
    }
  };

  // --- single unified render ---
  return (
    <div>
      {/* Bulk Upload Modal */}
      {showBulkModal && (
        <UploadEmployeeModal
          onClose={() => setShowBulkModal(false)}
          token={userDetails.token}
          orgId={userDetails.orgId}
        />
      )}
      {/* Single Header for all views - Hide when showing AddEmployee */}
      {!showAddEmployee && (
        <Header
          title={getHeaderTitle()}
          subtitle="Employee Information"
          onProfileClick={() => setActivePage && setActivePage("Profile")}
        />
      )}

      {/* Conditional Content Based on View */}
      {showAddEmployee ? (
        <AddEmployee
          onCancel={handleCancelAddEmployee}
          onSuccess={handleCancelAddEmployee}
          selectedEmployee={selectedEmployee}
          fetchEmployees={() => setRefetchTrigger(prev => prev + 1)}
        />
      ) : showTabs ? (
        <>
          {/* Tabs */}
          <div style={{
            display: "flex",
            gap: "240px",
            borderBottom: "2px solid #e0e0e0",
            backgroundColor: "#fff",
            padding: "0 19px",
            width: "1155px",
          }}>
            <button
              onClick={() => setActiveTab("profile")}
              style={{
                padding: "12px 24px",
                border: "none",
                backgroundColor: activeTab === "profile" ? "#fff" : "transparent",
                borderBottom: activeTab === "profile" ? "3px solid #2ca4d9" : "3px solid transparent",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: activeTab === "profile" ? "600" : "400",
                color: activeTab === "profile" ? "#2ca4d9" : "#666",
                transition: "all 0.3s ease"
              }}
            >
              Profile
            </button>
            <button
              onClick={() => setActiveTab("attendance")}
              style={{
                padding: "12px 24px",
                border: "none",
                backgroundColor: activeTab === "attendance" ? "#fff" : "transparent",
                borderBottom: activeTab === "attendance" ? "3px solid #2ca4d9" : "3px solid transparent",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: activeTab === "attendance" ? "600" : "400",
                color: activeTab === "attendance" ? "#2ca4d9" : "#666",
                transition: "all 0.3s ease"
              }}
            >
              Attendance
            </button>
            <button
              onClick={() => setActiveTab("payslip")}
              style={{
                padding: "12px 24px",
                border: "none",
                backgroundColor: activeTab === "payslip" ? "#fff" : "transparent",
                borderBottom: activeTab === "payslip" ? "3px solid #2ca4d9" : "3px solid transparent",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: activeTab === "payslip" ? "600" : "400",
                color: activeTab === "payslip" ? "#2ca4d9" : "#666",
                transition: "all 0.3s ease"
              }}
            >
              Pay Slip
            </button>
            <button
              onClick={() => setActiveTab("leave")}
              style={{
                padding: "12px 24px",
                border: "none",
                backgroundColor: activeTab === "leave" ? "#fff" : "transparent",
                borderBottom: activeTab === "leave" ? "3px solid #2ca4d9" : "3px solid transparent",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: activeTab === "leave" ? "600" : "400",
                color: activeTab === "leave" ? "#2ca4d9" : "#666",
                transition: "all 0.3s ease"
              }}
            >
              Leave
            </button>
          </div>
          {/* Tab Content */}
          <div>
            {renderTabContent()}
          </div>
        </>
      ) : (
        /* Table view */
        <div className='table-container'>
          <EmployeeFilter
            visible={showFilter}
            onClose={handleCloseFilter}
            onApply={handleApplyFilter}
          />
          {/* Top controls row */}
          <div className='table-controls'>
            {/* Search Input with Icon */}
            <div className="search-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <IoSearchOutline
                style={{
                  position: 'absolute',
                  left: '15px',
                  fontSize: '15px',
                  color: '#999'
                }}
              />
              <input
                type='text'
                className='search-input'
                placeholder='Search'
                value={searchTerm}
                onChange={handleSearchChange}
                /* Inga thaan neenga screenshot-la irukura maari width mathi irukkom */
                style={{
                  paddingLeft: '40px',
                  width: '145px',
                  height: '15px',
                }}
              />
            </div>
            <div className='button-container'>
              <button className='add-client-btn' onClick={() => setShowBulkModal(true)}>
                <CiCirclePlus /> Bulk Employee update
              </button>
              <button className='add-client-btn' onClick={handleAddEmployee}>
                <CiCirclePlus />Add Employee
              </button>
              <button className='filter-btn' onClick={handleFilter}>
                <BsSliders /> Filter
              </button>
            </div>
          </div>

          {/* Data table */}
          <table className='table' style={{ tableLayout: 'fixed', width: '100%' }}>
            <thead>
              <tr>
                <th className='th' style={{ width: '20%' }}>Employee Name</th>
                <th className='th' style={{ width: '20%' }}>Client Name</th>
                <th className='th' style={{ width: '15%' }}>Employee Key</th>
                <th className='th' style={{ width: '15%' }}>Work Site</th>
                <th className='th' style={{ width: '20%' }}>Job Role</th>
                <th className='th' style={{ width: '10%' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className='td' colSpan={6} style={{ textAlign: "center", padding: "20px" }}>
                    Loading employee data...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td className='td' colSpan={6} style={{ textAlign: "center", color: "red", padding: "20px" }}>
                    {error}
                  </td>
                </tr>
              ) : currentEmployees.length === 0 ? (
                <tr>
                  <td className='td' colSpan={6} style={{ textAlign: "center", padding: "20px" }}>
                    No matching employees
                  </td>
                </tr>
              ) : (
                currentEmployees.map((emp) => {
                  // Use employeeId for click and key
                  return (
                    <tr
                      key={emp.employeeId || emp.key}
                      onClick={() => handleEditEmployee(emp.employeeId)}
                      style={{ cursor: "pointer" }}
                    >
                      <td className='td' title={emp.name}>
                        {emp.name?.length > 20 ? `${emp.name.substring(0, 20)}...` : emp.name}
                      </td>
                      <td className='td' title={emp.clientName}>
                        {emp.clientName?.length > 20 ? `${emp.clientName.substring(0, 20)}...` : emp.clientName}
                      </td>
                      <td className='td'>{emp.key}</td>
                      <td className='td' title={emp.site}>
                        {emp.site?.length > 20 ? `${emp.site.substring(0, 20)}...` : emp.site}
                      </td>
                      <td className='td' title={emp.role}>
                        {emp.role?.length > 20 ? `${emp.role.substring(0, 20)}...` : emp.role}
                      </td>
                      <td className='td'>
                        <span
                          className={`status-badge ${emp.status?.toLowerCase() === "active"
                            ? "active"
                            : "inactive"
                            }`}
                        >
                          {emp.status}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {/* Footer: record range + pagination */}
          <div className='table-footer' style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '20px',
            padding: '15px 0',
            // borderTop: '1px solid #eee'
          }}>
            {/* left: record range */}
            <div className='record-count' style={{ fontSize: '14px', color: '#666' }}>
              Showing {totalElements === 0 ? 0 : indexOfFirstItem + 1}{" "}
              to {indexOfLastItem} of{" "}
              {totalElements} entries
            </div>

            {/* right: pagination buttons */}
            <div className='pagination' style={{ display: 'flex', gap: '5px' }}>
              {/* prev */}
              <button
                className='pagination-button'
                disabled={currentPage === 0}
                onClick={() => handlePageChange(currentPage - 1)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  backgroundColor: currentPage === 0 ? '#f5f5f5' : '#fff',
                  cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                  borderRadius: '4px',
                  opacity: currentPage === 0 ? 0.5 : 1
                }}
              >
                &lt;
              </button>

              {/* page numbers */}
              {getPageNumbers().map((page, idx) => {
                if (page === "...") {
                  return (
                    <span
                      key={`dots-${idx}`}
                      style={{
                        padding: '8px 12px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: '#666'
                      }}
                    >
                      ...
                    </span>
                  );
                }
                return (
                  <button
                    key={page}
                    className={`pagination-button ${currentPage === page ? "active" : ""}`}
                    onClick={() => handlePageChange(page)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      backgroundColor: currentPage === page ? '#232d5c' : '#fff',
                      color: currentPage === page ? '#fff' : '#000',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      minWidth: '40px'
                    }}
                  >
                    {page + 1}
                  </button>
                );
              })}

              {/* next */}
              <button
                className='pagination-button'
                disabled={currentPage >= totalPages - 1}
                onClick={() => handlePageChange(currentPage + 1)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  backgroundColor: currentPage >= totalPages - 1 ? '#f5f5f5' : '#fff',
                  cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                  borderRadius: '4px',
                  opacity: currentPage >= totalPages - 1 ? 0.5 : 1
                }}
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employee;
